// ── Shared pointer / touch / wheel input ──────────────────────────────────────
// Used by Gallery, GalleryAnimateZoom, and WallGallery.
//
// bindPointerEvents(gallery, vp, opts)
//   gallery          — the gallery instance (mutates velX/Y, zoomVel, etc.)
//   vp               — the viewport DOM element
//   hasCursor        — call gallery.setCursor() on mouse move (default false)
//   trackPinchStartZ — store gallery._pinchStartZ on pinch start (default false)
//
// applyInertia(gallery, dt)
//   dt — actual elapsed ms since last frame (frame-rate independent decay)

const ZOOM_VEL_MAX = 0.12;

// Shared button-zoom step. Larger jump per click than a wheel notch.
const ZOOM_STEP = 1.5;

export function stepZoom(gallery, direction) {
    gallery._crumbTrackingStarted = true;
    gallery._wake();
    const fitZ   = gallery.fitZ ?? gallery.ZOOM_LEVELS?.[0];
    const target = direction > 0 ? gallery.zoom * ZOOM_STEP : gallery.zoom / ZOOM_STEP;
    const bounded = direction > 0
        ? Math.min(target, gallery.ZOOM_MAX ?? Infinity)
        : Math.max(target, fitZ ?? gallery.ZOOM_MIN ?? 0);
    console.log('[zoom]', direction > 0 ? 'in' : 'out', { from: gallery.zoom, to: bounded, fitZ });
    if (Math.abs(bounded - gallery.zoom) < 0.001) return;
    gallery.animateZoom(bounded, gallery.W / 2, gallery.H / 2);
}

export function bindPointerEvents(gallery, vp, { hasCursor = false, trackPinchStartZ = false } = {}) {
    const pointers    = new Map();
    let hasDragged    = false;
    let dragStart     = null;
    let dragPrev      = null;
    let dragTime      = null;
    let pinchPrevDist = 0;
    let pinchPrevTime = 0;
    let pinchZoomVel  = 0;
    let pinchMidLast  = { x: 0, y: 0 };

    // `_dragging` deliberately becomes false for a two-finger gesture, so keep
    // pinch state separately. Renderers use this to defer DOM/image churn until
    // the fingers are up; without it a pinch was misclassified as "settled".
    gallery._pinching = false;

    const pinchInfo = () => {
        const [a, b] = [...pointers.values()];
        return {
            dist: Math.hypot(b.x - a.x, b.y - a.y),
            mid:  { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
        };
    };

    vp.addEventListener('pointerdown', e => {
        gallery._crumbTrackingStarted = true;
        vp.setPointerCapture(e.pointerId);
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        gallery.velX = gallery.velY = 0;

        if (pointers.size === 1) {
            gallery._dragging = true;
            hasDragged    = false;
            dragStart     = { x: e.clientX, y: e.clientY };
            dragPrev      = { x: e.clientX, y: e.clientY };
            dragTime      = performance.now();
            vp.classList.add('is-dragging');
        } else if (pointers.size === 2) {
            gallery._dragging = false;
            gallery._pinching = true;
            const info    = pinchInfo();
            pinchPrevDist = info.dist;
            pinchPrevTime = performance.now();
            pinchZoomVel  = 0;
            pinchMidLast  = info.mid;
            if (trackPinchStartZ) gallery._pinchStartZ = gallery.zoom;
        }
        gallery._wake();
    });

    vp.addEventListener('pointermove', e => {
        if (!pointers.has(e.pointerId)) return;
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

        if (pointers.size === 2) {
            const { dist, mid } = pinchInfo();
            if (pinchPrevDist > 0) {
                gallery.zoomAt(mid.x, mid.y, dist / pinchPrevDist);
                const now = performance.now();
                const dt  = now - pinchPrevTime || 1;
                pinchZoomVel  = Math.log(dist / pinchPrevDist) / dt;
                pinchPrevTime = now;
                pinchMidLast  = mid;
            }
            pinchPrevDist = dist;
            gallery._syncWorld();
            gallery._wake();
            return;
        }

        if (pointers.size === 1 && gallery._dragging) {
            const now = performance.now();
            const dt  = now - dragTime || 1;
            const dx  = e.clientX - dragPrev.x;
            const dy  = e.clientY - dragPrev.y;
            if (Math.abs(e.clientX - dragStart.x) > 4 || Math.abs(e.clientY - dragStart.y) > 4)
                hasDragged = true;
            gallery.panX += dx;
            gallery.panY += dy;
            gallery.velX  = dx / dt;
            gallery.velY  = dy / dt;
            dragPrev = { x: e.clientX, y: e.clientY };
            dragTime = now;
            gallery._syncWorld();
            return;
        }

        if (hasCursor && e.pointerType === 'mouse') gallery.setCursor(e.clientX, e.clientY);
    });

    vp.addEventListener('pointerup', e => {
        pointers.delete(e.pointerId);

        if (pointers.size === 1) {
            // Was pinching (2→1 fingers) — inject zoom momentum if recent and fast enough
            const age = performance.now() - pinchPrevTime;
            if (age < 150 && Math.abs(pinchZoomVel) > 0.001) {
                gallery.zoomVel = Math.max(-ZOOM_VEL_MAX, Math.min(ZOOM_VEL_MAX, pinchZoomVel * 16));
                gallery.zoomCX  = pinchMidLast.x;
                gallery.zoomCY  = pinchMidLast.y;
            }
            pinchPrevDist     = 0;
            gallery._pinching = false;
            gallery._dragging = false;
            gallery.velX      = gallery.velY = 0;
            hasDragged        = true;
            gallery._wake();
            return;
        }

        pinchPrevDist = 0;
        if (pointers.size === 0) {
            gallery._pinching = false;
            const clicked = gallery._dragging && !hasDragged;
            gallery._dragging = false;
            vp.classList.remove('is-dragging');
            if (clicked) gallery.handleClick(e.clientX, e.clientY);
            else if (hasCursor && e.pointerType === 'mouse') gallery.setCursor(e.clientX, e.clientY);
        }
    });

    vp.addEventListener('pointercancel', e => {
        pointers.delete(e.pointerId);
        pinchPrevDist = 0;
        if (pointers.size < 2) gallery._pinching = false;
        if (pointers.size === 0) {
            gallery._dragging = false;
            gallery.velX = gallery.velY = 0;
            vp.classList.remove('is-dragging');
        }
    });

    vp.addEventListener('wheel', e => {
        e.preventDefault();
        gallery._crumbTrackingStarted = true;

        // Ignore wheel events while touch pointers are active — iOS Safari
        // fires ctrlKey+wheel on native pinch gestures which would race the
        // two-pointer pinch handler and trigger runaway zoom.
        if (pointers.size > 0) return;

        // Trackpad pinch — browsers set ctrlKey on zoom gestures.
        // Clamp deltaY so fast pinches can't jump zoom by huge multipliers per event.
        if (e.ctrlKey) {
            const dy = Math.max(-50, Math.min(50, e.deltaY));
            const factor = Math.exp(-dy * 0.01);
            gallery.zoomAt(e.clientX, e.clientY, factor);
            gallery._syncWorld();
            gallery._wake();
            return;
        }

        // Trackpad two-finger pan — deltaX nonzero, or small deltaY steps typical of trackpads.
        // Physical mouse wheels produce deltaX === 0 and larger deltaY (≥100 usually).
        const isTrackpadPan = e.deltaX !== 0 || (e.deltaMode === 0 && Math.abs(e.deltaY) < 50);
        if (isTrackpadPan) {
            gallery.panX -= e.deltaX;
            gallery.panY -= e.deltaY;
            gallery.velX  = 0;
            gallery.velY  = 0;
            gallery._syncWorld();
            gallery._wake();
            return;
        }

        // Physical mouse wheel — step zoom with inertia.
        const delta = e.deltaMode === 1 ? e.deltaY * 30 : e.deltaY;
        gallery.zoomVel = Math.max(-ZOOM_VEL_MAX, Math.min(ZOOM_VEL_MAX,
            gallery.zoomVel + (delta < 0 ? 0.07 : -0.07)));
        gallery.zoomCX = e.clientX;
        gallery.zoomCY = e.clientY;
        gallery._wake();
    }, { passive: false });
}

export function applyInertia(gallery, dt = 16) {
    if (gallery._dragging) return;
    const panDecay  = Math.pow(0.88, dt / 16);
    const zoomDecay = Math.pow(0.78, dt / 16);

    gallery.panX += gallery.velX * dt;
    gallery.panY += gallery.velY * dt;
    gallery.velX *= panDecay;
    gallery.velY *= panDecay;
    if (Math.abs(gallery.velX) < 0.001) gallery.velX = 0;
    if (Math.abs(gallery.velY) < 0.001) gallery.velY = 0;

    if (Math.abs(gallery.zoomVel) > 0.0005) {
        gallery.zoomAt(gallery.zoomCX, gallery.zoomCY, Math.exp(gallery.zoomVel));
        gallery.zoomVel *= zoomDecay;
    } else {
        gallery.zoomVel = 0;
    }

    gallery._syncWorld();
    gallery._maybeIdle();
}
